import { inject, Injectable } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import {
  AppointmentModel,
  arrayEqual,
  Clinic,
  EstimateFormGroup,
  Facility,
  FacilityService,
  PermissionsService,
} from '@mmx/shared'
import { combineLatest, Observable } from 'rxjs'
import { distinctUntilChanged, map, startWith } from 'rxjs/operators'

import { EstimateBalanceStateService } from './estimate-balance-state.service'

@Injectable()
export class ClaimSearchController {
  private readonly facilityService = inject(FacilityService)
  private readonly permissions = inject(PermissionsService)
  private readonly state = inject(EstimateBalanceStateService)
  // Bridge state signals to observables once, here in the service's injection
  // context, so prefillQuery$() (invoked later from ngOnInit) can compose them.
  private readonly estimate$ = toObservable(this.state.estimate)
  private readonly appointments$ = toObservable(this.state.appointments)

  enabled$(estimateForm: EstimateFormGroup, clinic: () => Clinic | null): Observable<boolean> {
    const selectedFacility$ = this.selectedFacility$(estimateForm)
    return combineLatest([this.permissions.hasPermission('estimate.model-claim-search'), selectedFacility$]).pipe(
      map(([hasPermission, facility]) => this.computeEnabled(clinic(), hasPermission, facility)),
      distinctUntilChanged(),
    )
  }

  // HB facilities pre-fill the model-claim search with the selected appointments'
  // CPT codes. PB facilities resolve charges directly and never pre-fill.
  prefillQuery$(estimateForm: EstimateFormGroup): Observable<string | null> {
    const selectedFacility$ = this.selectedFacility$(estimateForm)
    const selectedAppointmentIds$ = this.estimate$.pipe(
      map(estimate => estimate?.appointmentIds ?? []),
      distinctUntilChanged(arrayEqual<string>),
    )
    return combineLatest([this.appointments$, selectedAppointmentIds$, selectedFacility$]).pipe(
      map(([appointments, ids, facility]) => this.buildQuery(appointments, ids, facility)),
      distinctUntilChanged(),
    )
  }

  private selectedFacility$(estimateForm: EstimateFormGroup): Observable<Facility | null> {
    const facilityId$ = estimateForm.controls.facilityId.valueChanges.pipe(
      startWith(estimateForm.controls.facilityId.value),
      distinctUntilChanged(),
    )
    // facilities$ is included so the computation re-fires when FacilityService
    // finishes loading — otherwise the initial emission sees allFacilities=[]
    // and distinctUntilChanged traps `false` until facilityId changes again.
    return combineLatest([facilityId$, this.facilityService.facilities$]).pipe(
      map(([facilityId]) => this.facilityService.allFacilities.find(f => f.id === facilityId) ?? null),
      distinctUntilChanged(),
    )
  }

  private computeEnabled(clinic: Clinic | null, hasPermission: boolean, facility: Facility | null): boolean {
    const hasFacilityConfig = !!facility?.mmx?.hbFacilityId || !!facility?.mmx?.pbFacilityId
    return !!clinic?.pricer?.claimSearch && hasPermission && hasFacilityConfig
  }

  private buildQuery(appointments: AppointmentModel[], appointmentIds: string[], facility: Facility | null): string | null {
    const isHbFacility = !!facility?.mmx?.hbFacilityId
    if (!isHbFacility || appointmentIds.length === 0) {
      return null
    }
    const idSet = new Set(appointmentIds)
    const codes = new Set<string>()
    for (const appointment of appointments) {
      if (!idSet.has(appointment.id)) {
        continue
      }
      for (const code of appointment.getCptCodes?.() ?? []) {
        if (code) {
          codes.add(code)
        }
      }
    }
    return codes.size > 0 ? Array.from(codes).join('|') : null
  }
}
